import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";

import PromoBanner from "./PromoBanner";
import Navbar from "./Navbar";
import Slider from "./Slider";
import Engagements from "./Engagements";
import Categories from "./Categories";
import Newsletter from "./Newsletter";
import Footer from "./Footer";
import ProductCard from "./ProductCard";

import slideImage from "./assets/slide1.png";
import slideImage2 from "./assets/slide2.png";

const slidesData = [
  {
    image: slideImage,
    eyebrow: "Maison Deenzine",
    title: "Le fil devient sac.\nLe geste devient style.",
    subtitle:
      "Des sacs en crochet faits main, pensés comme des pièces fortes, élégantes et singulières.",
    buttonText: "Découvrir la collection",
    buttonLink: "/products",
    secondaryText: "Voir le déstockage",
    secondaryLink: "/destockage",
  },
  {
    image: slideImage2,
    eyebrow: "Séries limitées",
    title: "Des pièces rares,\ncréées lentement.",
    subtitle:
      "Chaque modèle est imaginé autour de la matière, du détail et du geste artisanal.",
    buttonText: "Voir le déstockage",
    buttonLink: "/destockage",
    secondaryText: "Voir la collection",
    secondaryLink: "/products",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 34 },
  visible: { opacity: 1, y: 0 },
};

const normalizeImage = (image) => {
  if (!image) return "";
  if (image.startsWith("http")) return image;
  if (image.startsWith("/")) return image;
  return `/${image}`;
};

const ScrollStitchObserver = () => {
  useEffect(() => {
    const targets = document.querySelectorAll(
      ".stitch-reveal, .stitch-card, .thread-product-item, .section-heading, .categories, .engagements, .newsletter"
    );

    if (!targets.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-stitched");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -12% 0px" }
    );

    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, []);

  return null;
};

/* Points-clés où le fil change de direction — nœuds de broderie */
const THREAD_PATH = "M600 0 C520 230 720 330 596 560 C496 745 720 910 620 1120 C520 1330 420 1450 540 1680 C670 1930 822 2050 690 2310 C560 2565 368 2680 520 2940 C675 3200 882 3330 702 3620 C570 3838 462 3970 560 4200 C635 4375 782 4430 668 4700";

const KNOTS = [
  { cx: 596,  cy: 560,  r: 5.5, thresh: 0.12 },
  { cx: 620,  cy: 1120, r: 6,   thresh: 0.24 },
  { cx: 540,  cy: 1680, r: 5.5, thresh: 0.36 },
  { cx: 690,  cy: 2310, r: 6,   thresh: 0.49 },
  { cx: 520,  cy: 2940, r: 5.5, thresh: 0.63 },
  { cx: 702,  cy: 3620, r: 6,   thresh: 0.77 },
  { cx: 560,  cy: 4200, r: 5.5, thresh: 0.89 },
];

const SiteThread = () => {
  const wrapRef = useRef(null);
  const drawPathRef = useRef(null);
  const ghostPathRef = useRef(null);
  const needleRef = useRef(null);
  const knotsRef = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const drawPath = drawPathRef.current;
    const ghostPath = ghostPathRef.current;
    const needle = needleRef.current;

    if (!wrap || !drawPath || !ghostPath || !needle) return undefined;

    const length = drawPath.getTotalLength();
    [drawPath, ghostPath].forEach((path) => {
      path.style.strokeDasharray = String(length);
      path.style.strokeDashoffset = String(length);
    });

    const update = () => {
      const rect = wrap.getBoundingClientRect();
      const viewport = window.innerHeight || 1;
      const progress = Math.min(
        Math.max((viewport * 0.78 - rect.top) / (rect.height - viewport * 0.25), 0),
        1
      );
      const eased = 1 - Math.pow(1 - progress, 2.4);
      const offset = length * (1 - eased);

      drawPath.style.strokeDashoffset = String(offset);
      ghostPath.style.strokeDashoffset = String(offset);
      wrap.style.setProperty("--thread-progress", eased.toFixed(4));

      /* nœuds : pop quand le fil les atteint */
      KNOTS.forEach((k, i) => {
        const el = knotsRef.current[i];
        if (!el) return;
        if (eased >= k.thresh) el.classList.add("is-visible");
        else el.classList.remove("is-visible");
      });

      const point = drawPath.getPointAtLength(Math.max(0, Math.min(length, length * eased)));
      const prevPoint = drawPath.getPointAtLength(Math.max(0, Math.min(length, length * eased - 5)));
      const angle = Math.atan2(point.y - prevPoint.y, point.x - prevPoint.x) * (180 / Math.PI);

      needle.setAttribute("transform", `translate(${point.x} ${point.y}) rotate(${angle})`);
      needle.style.opacity = progress > 0.01 && progress < 0.995 ? "1" : "0";

      rafRef.current = null;
    };

    const requestUpdate = () => {
      if (rafRef.current) return;
      rafRef.current = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);

    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
    };
  }, []);

  return (
    <div className="site-thread-final" ref={wrapRef} aria-hidden="true">
      <svg viewBox="0 0 1200 4700" preserveAspectRatio="none">
        <defs>
          {/* gradient fil de soie : cuivre → or brillant → cuivre */}
          <linearGradient id="threadGrad" x1="0" y1="0" x2="0" y2="4700" gradientUnits="userSpaceOnUse">
            <stop offset="0%"   stopColor="#e8b87a" />
            <stop offset="15%"  stopColor="#c98a52" />
            <stop offset="32%"  stopColor="#f0d090" />
            <stop offset="50%"  stopColor="#c98a52" />
            <stop offset="68%"  stopColor="#e8c070" />
            <stop offset="84%"  stopColor="#ad5d39" />
            <stop offset="100%" stopColor="#c98a52" />
          </linearGradient>
          {/* gradient aiguille : acier poli */}
          <linearGradient id="needleGrad" x1="-50" y1="0" x2="58" y2="0" gradientUnits="userSpaceOnUse">
            <stop offset="0%"   stopColor="#b8b0a4" />
            <stop offset="30%"  stopColor="#e8e2da" />
            <stop offset="60%"  stopColor="#f0ece6" />
            <stop offset="100%" stopColor="#c8c0b4" />
          </linearGradient>
          {/* halo lumineux autour des nœuds */}
          <radialGradient id="knotGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#f0d090" stopOpacity="0.7" />
            <stop offset="60%"  stopColor="#cda36e" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#ad5d39" stopOpacity="0" />
          </radialGradient>
          {/* flou lumineux en bout de fil */}
          <filter id="threadTip" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* tracé fantôme (guide) */}
        <path className="site-thread-base-final" d={THREAD_PATH} />

        {/* pointillé d'aiguille */}
        <path ref={ghostPathRef} className="site-thread-ghost-final" d={THREAD_PATH} />

        {/* fil dessiné — gradient cuivre-or */}
        <path ref={drawPathRef} className="site-thread-draw-final" d={THREAD_PATH} />

        {/* nœuds de broderie aux virages du fil */}
        {KNOTS.map((k, i) => (
          <g key={i} ref={(el) => { knotsRef.current[i] = el; }} className="thread-knot-group">
            {/* halo */}
            <circle className="thread-knot-halo" cx={k.cx} cy={k.cy} r={k.r * 3.2} fill="url(#knotGlow)" />
            {/* corps du nœud */}
            <circle className="thread-knot" cx={k.cx} cy={k.cy} r={k.r} />
            {/* mini-croix brodée */}
            <line className="thread-knot-cross" x1={k.cx - k.r * 0.7} y1={k.cy} x2={k.cx + k.r * 0.7} y2={k.cy} />
            <line className="thread-knot-cross" x1={k.cx} y1={k.cy - k.r * 0.7} x2={k.cx} y2={k.cy + k.r * 0.7} />
          </g>
        ))}

        {/* aiguille qui suit le fil */}
        <g ref={needleRef} className="scroll-needle-final">
          {/* corps : pointe effilée à droite, tête arrondie à gauche */}
          <path d="M-42,-1.4 C-10,-2.2 28,-1.6 62,0 C28,1.6 -10,2.2 -42,1.4 Z" />
          {/* chas de l'aiguille */}
          <ellipse cx="-30" cy="0" rx="7" ry="2.2" />
          {/* reflet lumineux principal */}
          <path className="needle-highlight" d="M-22,-0.8 C8,-1.4 32,-1 56,-.1" />
          {/* second reflet subtil */}
          <path className="needle-highlight" style={{opacity:0.4}} d="M-20,0.6 C10,0.2 34,0.4 56,0.2" />
        </g>
      </svg>
    </div>
  );
};

const StitchBlock = ({ number, eyebrow, title, text, side = "left", children }) => (
  <motion.section
    className={`stitch-block stitch-block-${side} stitch-reveal`}
    variants={fadeUp}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.25 }}
    transition={{ duration: 0.72, ease: [0.22, 1, 0.36, 1] }}
  >
    <span className="stitch-number">{number}</span>
    <div className="stitch-card">
      <span className="eyebrow">{eyebrow}</span>
      <h2 className="section-title">{title}</h2>
      <p className="section-text">{text}</p>
      {children}
    </div>
  </motion.section>
);

const Website = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const bestSellers = useMemo(() => products.slice(0, 6), [products]);

  useEffect(() => {
    let active = true;

    const fetchProducts = async () => {
      try {
        const res = await fetch("/api/products");
        const data = await res.json();
        if (!active) return;
        setProducts(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Erreur chargement produits :", error);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchProducts();
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="page page-home home-final-page">
      <Helmet>
        <title>Deenzine – Sacs en crochet faits main | Séries limitées</title>
        <meta name="description" content="Deenzine crée des sacs en crochet faits main, élégants et en séries limitées. Pièces uniques, matières soigneusement choisies, expédition rapide en France." />
        <link rel="canonical" href="https://www.deenzine.com/" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Deenzine" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:url" content="https://www.deenzine.com/" />
        <meta property="og:title" content="Deenzine – Sacs en crochet faits main | Séries limitées" />
        <meta property="og:description" content="Des sacs en crochet faits main, pensés comme des pièces fortes, élégantes et singulières. Séries limitées, expédition rapide." />
        <meta property="og:image" content="https://www.deenzine.com/images/og-deenzine.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Deenzine – Sacs en crochet faits main" />
        <meta name="twitter:description" content="Des sacs en crochet faits main, élégants et en séries limitées." />
        <meta name="twitter:image" content="https://www.deenzine.com/images/og-deenzine.jpg" />

        {/* JSON-LD : LocalBusiness / OnlineStore */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "OnlineStore",
          "name": "Deenzine",
          "url": "https://www.deenzine.com/",
          "description": "Maison créative spécialisée dans les sacs en crochet faits main, élégants et en séries limitées.",
          "logo": "https://www.deenzine.com/images/logo.svg",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Sacs en crochet faits main",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Sacs crochet semi-rigide" } },
              { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Sacs crochet rigide" } },
              { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Sacs crochet souple fin" } }
            ]
          }
        })}</script>
      </Helmet>

      <PromoBanner />
      <Navbar />

      <main className="home-final-main">
        <ScrollStitchObserver />
        <SiteThread />
        <Slider slides={slidesData} logoSrc="/images/logo.svg" />

        <section className="home-editorial-intro section">
          <div className="container home-editorial-grid stitch-reveal">
            <div className="home-editorial-kicker">
              <span>01</span>
              <p>Le fil comme direction artistique</p>
            </div>
            <div>
              <span className="eyebrow">Maison artisanale</span>
              <h1 className="display-title">
                Une maison de fil, pensée comme une pièce de mode.
              </h1>
            </div>
            <p className="lead">
              Des sacs en crochet faits main, présentés dans un univers sobre, précis et raffiné. Le fil devient une signature : il relie les matières, les pièces et le geste artisanal avec discrétion.
            </p>
          </div>
        </section>

        <section className="stitch-journey-final">
          <div className="container">
            <StitchBlock
              number="02"
              eyebrow="Savoir-faire"
              title="Le geste se révèle avec retenue."
              text="Au scroll, un trait fin accompagne la lecture, dessine les contours et révèle les contenus comme une couture discrète sur une toile claire."
              side="left"
            >
              <div className="home-intro-pills">
                <span className="mini-pill">Fil conducteur</span>
                <span className="mini-pill">Rythme calme</span>
                <span className="mini-pill">Détail matière</span>
              </div>
            </StitchBlock>

            <StitchBlock
              number="03"
              eyebrow="Direction premium"
              title="Des lignes fines, des matières, du silence."
              text="Une composition éditoriale, des respirations larges, des touches argile et olive, et des animations lentes pour garder une sensation premium."
              side="right"
            >
              <div className="stitch-detail-card">
                <span className="stitch-detail-icon" />
                <p>Ombres légères, traits fins, détails cousus et transitions souples.</p>
              </div>
            </StitchBlock>
          </div>
        </section>

        <section className="section section-products final-products-section">
          <div className="container">
            <div className="section-heading final-section-heading stitch-reveal">
              <div>
                <span className="eyebrow">Sélection</span>
                <h2 className="section-title">Les pièces essentielles</h2>
                <p className="section-text">
                  Une sélection épurée de sacs faits main, mise en scène avec de l’espace, de la matière et des détails précis.
                </p>
              </div>

              <a href="/products" className="button button-secondary">
                Voir toute la collection
              </a>
            </div>

            {loading ? (
              <div className="empty-state">Chargement des produits…</div>
            ) : bestSellers.length === 0 ? (
              <div className="empty-state">Aucun produit pour le moment.</div>
            ) : (
              <div className="products-grid final-products-grid">
                {bestSellers.map((product, index) => (
                  <motion.div
                    key={product.id}
                    className="thread-product-item stitch-reveal"
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.18 }}
                    transition={{ duration: 0.55, delay: index * 0.055 }}
                  >
                    <ProductCard
                      productId={product.id}
                      image={normalizeImage(product.image)}
                      name={product.name}
                      price={product.price}
                      promo_price={product.promo_price}
                      promo_active={product.promo_active}
                      colors={product.colors}
                    />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="section final-brand-section">
          <div className="container">
            <motion.div
              className="final-brand-card stitch-card stitch-reveal"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.75 }}
            >
              <div>
                <span className="eyebrow">L’esprit Deenzine</span>
                <h2 className="section-title">Une maison créative contemporaine.</h2>
              </div>
              <p className="section-text">
                Le fil brode le logo à l’entrée, puis continue son chemin dans la page. Il devient une signature visuelle subtile, jamais décorative, toujours liée à la matière.
              </p>
              <div className="brand-story-actions">
                <a href="/products" className="button">Explorer la collection</a>
                <a href="/contact" className="text-link">Nous contacter</a>
              </div>
            </motion.div>
          </div>
        </section>

        <Categories />
        <Engagements />
        <Newsletter />
      </main>

      <Footer />
    </div>
  );
};

export default Website;